/**
 * AI textarea for the Recommend page.
 *
 * Time-boxed feature: the user types a free-text query ("I want to spend 50k
 * on flights to Mumbai next week"), we call /api/recommend/parse-intent and
 * the structured form below fills in. The user can edit the chips afterwards
 * before running the engine.
 *
 * The LLM ONLY parses. It never picks a card. The deterministic engine still
 * runs over the user's wallet exactly as if the user had typed the structured
 * inputs themselves. Goal: lower the barrier for naive users without losing
 * the engine's truth.
 */
import { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Recommend } from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export const AiIntentBox = ({ onParsed }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleParse = async () => {
    if (!text.trim() || loading) return;
    setLoading(true);
    try {
      const parsed = await Recommend.parseIntent(text);
      if ((parsed.confidence || 0) < 0.3) {
        toast.warning("Couldn't parse that confidently. Try typing the amount and merchant directly below.");
      } else {
        toast.success(`Understood: ${parsed.notes || 'parsed'}`);
        onParsed(parsed);
      }
    } catch (e) {
      console.error('parse-intent failed', e);
      toast.error('AI parser is taking a break. Use the form below.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-border/70 rounded-xl bg-muted/30 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/50 transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid="recommend-ai-toggle"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-accent" />
          <span className="text-sm font-medium text-foreground">Try natural language</span>
          <Badge variant="outline" className="text-[9px] uppercase tracking-wider h-4 px-1.5">
            Beta
          </Badge>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      <div className={cn('overflow-hidden transition-all', open ? 'max-h-[260px]' : 'max-h-0')}>
        <div className="px-4 pb-4 pt-1 space-y-2.5">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. I'm spending 50k on flights to Dubai next week"
            className="resize-none h-20"
            data-testid="recommend-ai-textarea"
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] text-muted-foreground">
              We extract structured fields below. The deterministic engine then picks the card.
            </p>
            <Button
              size="sm"
              onClick={handleParse}
              disabled={!text.trim() || loading}
              data-testid="recommend-ai-parse-button"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              Parse with AI
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
